<template>
    <div class="WorkOrderSiteSelector">
        <div class="header">
            <FormInput v-model="search" placeholder="Search for site..." @input="handleSearch"/>
        </div>

        <div class="actions">
            <span v-if="siteIds.size > 0">{{ siteIds.size }} selected</span><span v-else>No limitations set</span>
            <div class="buttons">
                <Button @click="handleSelectAll" :disabled="isSelectAllDisabled">Select All</Button>
                <Button @click="handleClearAll" :disabled="siteIds.size === 0">Clear All</Button>
            </div>
        </div>

        <div class="list">
            <button class="item item-add" @click="handleCreate"><Icon name="plus"/>Add new</button>
            <button
                v-for="site in filteredSites"
                :key="site.id"
                class="item"
                :class="{ selected: isSiteSelected(site) }"
                @click="handleSelectSite(site, isSiteSelected(site))"
            >
                <div>{{ site.name }}</div>
                <span>{{ formatSiteAddress(site) }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import SiteCreateModal from '@/components/modals/SiteCreateModal';

export default {
    name: 'WorkOrderSiteSelector',

    props: {
        sites: { type: Array, default: () => [] },
    },

    data: () => ({
        search: '',

        siteIds: new Set(),
        filteredSites: [],
    }),

    computed: {
        ...mapState('company', { allSites: 'sites' }),

        isSelectAllDisabled() {
            // Disable if no sites available or all filtered sites are already selected
            return this.filteredSites.length === 0 ||
                   this.filteredSites.every(site => this.siteIds.has(site.id));
        },
    },

    methods: {
        formatSiteAddress(site) {
            const parts = [];

            if (site.address1) parts.push(site.address1);
            if (site.address2) parts.push(site.address2);

            const cityStateZip = [];
            if (site.city) cityStateZip.push(site.city);
            if (site.state) cityStateZip.push(site.state);
            if (site.zip) cityStateZip.push(site.zip);

            if (cityStateZip.length > 0) {
                parts.push(cityStateZip.join(', '));
            }

            return parts.length > 0 ? parts.join(', ') : 'Missing address';
        },

        isSiteSelected(site) {
            return this.siteIds.has(site.id);
        },

        handleSelectSite(site, isSelected) {
            this.$analytics.trackClick(this.$options.name, 'select_site', 'unknown', {
                action_type: 'select',
                model_type: 'site',
                site_id: site.id,
                is_selected: isSelected,
            });

            if (isSelected) {
                this.siteIds.delete(site.id);
            } else {
                this.siteIds.add(site.id);
            }

            const sites = [...this.siteIds]
                .map(id => this.allSites.find(s => s.id === id))
                .filter(site => site);

            this.updateSites(sites);
        },

        handleSearch(value) {
            if (!value) {
                this.resetSiteList();
                return;
            }

            this.filteredSites = this.filteredSites.filter((site) => {
                const siteName = site.name.toLowerCase().trim();
                const searchInput = value.toLowerCase().trim();
                return siteName.includes(searchInput);
            });
        },

        resetSiteList() {
            this.filteredSites = [...this.allSites];
        },

        updateSites(sites) {
            // Force component to reload
            this.filteredSites = [...this.filteredSites];

            this.$emit('update:sites', sites);
        },

        handleClearAll() {
            this.$analytics.trackButtonClick(this.$options.name, 'clear_all', {
                model_type: 'site',
            });

            this.siteIds.clear();
            this.updateSites([]);
        },

        handleSelectAll() {
            // Add all filtered sites to selection
            this.filteredSites.forEach(site => {
                this.siteIds.add(site.id);
            });

            const sites = [...this.siteIds]
                .map(id => this.allSites.find(s => s.id === id))
                .filter(site => site);

            this.$analytics.trackButtonClick(this.$options.name, 'select_all', {
                model_type: 'site',
                selected_amount: sites.length,
            });

            this.updateSites(sites);
        },

        handleCreate() {
            this.$analytics.trackButtonClick(this.$options.name, 'add_new_site', {
                model_type: 'site',
                model_action: 'create_intent',
            });

            this.$modal.show(SiteCreateModal, {
                onSiteCreated: this.handleSiteCreated,
            });
        },

        handleSiteCreated(newSite) {
            // Add the new site to the filtered list immediately
            this.filteredSites.unshift(newSite);

            // Add to selected sites
            const newSites = [...this.sites, newSite];
            this.siteIds.add(newSite.id);
            this.updateSites(newSites);
        },
    },

    mounted() {
        this.sites.forEach(site => {
            this.siteIds.add(site.id);
        })

        this.resetSiteList();
    },
};
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.WorkOrderSiteSelector {
    .actions {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .buttons {
            display: flex;
        }
    }

    .list {
        border: 1px solid $divider-color;
        height: 300px;
        overflow-y: scroll;
    }

    .item {
        @extend %form-control;
        margin: 2px 0;
        border: none;
        border-radius: 0;
        text-align: left;
        cursor: pointer;

        &.item-add {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        &.selected {
            background-color: Highlight;
            color: HighlightText;

            &:hover {
                opacity: 1;
            }
        }

        &:hover {
            background-color: Highlight;
            color: HighlightText;
            opacity: 0.8;
        }

        span {
            font-size: $text-color-muted;
            font-size: 12px;
        }
    }

    .FormInputContainer {
        margin-bottom: $grid-gutter-width;
    }
}
</style>